/**
 * CreateAccountScreen
 * Screen for creating a new account during onboarding
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { CreateAccountScreenProps } from '../navigation/types';
import { useGoals } from '../hooks/useGoals';
import { THEME_COLORS } from '../models/GoalAccount';

export const CreateAccountScreen: React.FC<CreateAccountScreenProps> = ({ navigation }) => {
  const { createAccountSafe, switchAccount } = useGoals();
  const [accountName, setAccountName] = useState('');
  const [selectedColor, setSelectedColor] = useState(THEME_COLORS[0].color);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    // Validate
    const trimmedName = accountName.trim();
    if (trimmedName.length === 0) {
      Alert.alert('Error', 'Please enter a journey name');
      return;
    }

    try {
      setIsCreating(true);

      // Create account with selected theme color
      const newAccount = await createAccountSafe(trimmedName, selectedColor);
      console.log('Account created:', newAccount.id);

      // Switch to the newly created account
      await switchAccount(newAccount.id);
      console.log('Switched to account:', newAccount.id);

      // Navigate to create main goal, passing the account ID
      console.log('Navigating to CreateMainGoal with accountId:', newAccount.id);
      navigation.replace('CreateMainGoal', { accountId: newAccount.id });
    } catch (error) {
      Alert.alert('Error', error instanceof Error ? error.message : 'Failed to create journey');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Create Your Journey</Text>
            <Text style={styles.subtitle}>
              Give your journey a name. You can create multiple journeys later to track different
              areas of your life.
            </Text>
          </View>

          {/* Input */}
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Journey Name</Text>
            <TextInput
              style={styles.input}
              placeholder="e.g., Mind, Body, Soul"
              placeholderTextColor="#999"
              value={accountName}
              onChangeText={setAccountName}
              autoFocus
              autoCapitalize="words"
              returnKeyType="next"
              onSubmitEditing={handleCreate}
              editable={!isCreating}
            />
          </View>

          {/* Color Picker */}
          <View style={styles.colorPickerContainer}>
            <Text style={styles.label}>Theme Color</Text>
            <View style={styles.colorGrid}>
              {THEME_COLORS.map((theme) => (
                <TouchableOpacity
                  key={theme.color}
                  style={[
                    styles.colorOption,
                    { backgroundColor: theme.color },
                    selectedColor === theme.color && styles.colorOptionSelected,
                  ]}
                  onPress={() => setSelectedColor(theme.color)}
                  disabled={isCreating}
                >
                  {selectedColor === theme.color && (
                    <Text style={styles.checkmark}>✓</Text>
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* CTA */}
          <TouchableOpacity
            style={[styles.createButton, isCreating && styles.createButtonDisabled]}
            onPress={handleCreate}
            disabled={isCreating}
          >
            {isCreating ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.createButtonText}>Continue</Text>
            )}
          </TouchableOpacity>

          {/* Helper text */}
          <Text style={styles.helperText}>
            Examples: "Mind", "Body", "Soul", "Creative", "Social"
          </Text>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  keyboardView: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 24,
    justifyContent: 'center',
  },
  header: {
    marginBottom: 32,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: '#333',
    marginBottom: 12,
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
  },
  inputContainer: {
    marginBottom: 24,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 16,
    fontSize: 16,
    color: '#333',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  createButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 16,
  },
  createButtonDisabled: {
    opacity: 0.6,
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  helperText: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    lineHeight: 20,
  },
  colorPickerContainer: {
    marginBottom: 24,
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  colorOption: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: 'transparent',
  },
  colorOptionSelected: {
    borderColor: '#333',
    borderWidth: 3,
  },
  checkmark: {
    fontSize: 24,
    color: '#fff',
    fontWeight: '700',
  },
});
